// ── Task board page ──

import { api } from '../api.js';
import { checkAuth, getUser } from '../auth.js';
import { showToast, openModal, closeModal } from '../ui.js';
import { sprintOptionsHtml, renderMarkdown } from '../utils.js';
import { initLayout, getContentEl, updateLayoutContext } from '../layout.js';
import { renderViewSwitcher, bindViewSwitcher, getViewPref, setViewPref, updateViewSwitcherLabel } from '../components/view-switcher.js';
import { closeTaskDetail } from '../components/task-detail.js';
import { removeAssigneePicker } from '../components/assignee-picker.js';
import { renderMemberCombo, createMemberCombo } from '../components/member-combo.js';
import { attachImageHandlers } from '../lib/image-paste.js';
import { attachEmbedControls } from '../lib/embed-menu.js';
import { renderBoardFilters, bindBoardFilters, unbindBoardFilters, applyFilters, hasActiveFilters, hasFilterParams, filtersToParams, filtersFromParams, FILTER_DEFAULTS, sprintFilterNumbers, applySprintPresets, syncFilterChips, captureFilterFocus, restoreFilterFocus } from '../components/board-filters.js';
import { getQueryParams, setQueryParams } from '../router.js';
import { saveFilterPrefs, loadFilterPrefs, clearFilterPrefs } from '../lib/board-filter-prefs.js';
import { renderKanbanView } from './kanban.js';
import { renderSprintView } from './sprint-board.js';
import { openSprintManager } from '../components/sprint-manager.js';
import { openTrelloImportModal } from '../components/trello-import-modal.js';
import { openTrelloCardPicker } from '../components/trello-card-picker.js';
import { openGmailThreadPicker } from '../components/gmail-thread-picker.js';
import { openJiraIssuePicker } from '../components/jira-issue-picker.js';
import { closeImportPicker } from '../lib/import-picker-shell.js';
import { reserveTaskKey, releaseTaskKey } from '../lib/task-key-reservation.js';
import { loadStatuses, statusNames } from '../lib/statuses.js';
import { loadProject } from '../lib/project.js';
import { attachTagTypeahead } from '../lib/tag-typeahead.js';
import { loadNewTaskDraft, saveNewTaskDraft, clearNewTaskDraft, attachmentDeletePath } from '../lib/task-draft.js';
import { groupLabelPlural, groupLabelSingular } from '../lib/task-group-label.js';
import { sprintsEnabled, defaultSprintNumber } from '../lib/sprints-enabled.js';

let state = {
  projectId: null,
  project: null,
  tasks: [],
  members: [],
  sprints: [],
  statuses: [],
  filters: { ...FILTER_DEFAULTS },
  activeView: 'sprint',
  subViewCleanup: null,
  // Edit Board (C1183) — kanban column-header drag reorder, off by default every mount.
  editBoardMode: false,
};

function resetState() {
  state = {
    projectId: null,
    project: null,
    tasks: [],
    members: [],
    sprints: [],
    statuses: [],
    filters: { ...FILTER_DEFAULTS },
    activeView: 'sprint',
    subViewCleanup: null,
    editBoardMode: false,
  };
}

// Owner or editor may reorder the workflow from the board (mirrors the API's own
// requireWriteAccess: isOwner || memberRole === 'editor' — routes/statuses.js). Derived
// from the members list already fetched below rather than a new endpoint: GET
// /members returns the owner's own row with role:'owner' (routes/projects.js), so no
// separate is_owner lookup is needed. `!me` (self not found in the list at all) fails
// open rather than hiding the control — the PUT /reorder call is the real backstop.
function canEditWorkflow() {
  const user = getUser();
  if (!user) return false;
  const me = state.members.find(m => (m.user_id || m.id) === user.id);
  return !me || me.role === 'owner' || me.role === 'editor';
}

// C1334 — project.sprints_enabled gate. Safe before the project fetch resolves
// (state.project === null → sprintsEnabled() reads that as ON, see lib/sprints-enabled.js).
function sprintsOn() {
  return sprintsEnabled(state.project);
}

// ── Create task modal ──

// applyImportPrefill(overlay, { title, description }) — writes a picked import source's
// content (Trello card — C1448, Gmail thread — C1449) into the still-open New Task modal.
// Overwrites both fields (user decision — no merge with anything already typed).
function applyImportPrefill(overlay, { title, description }) {
  const titleEl = overlay.querySelector('#task-title');
  const descEl = overlay.querySelector('#task-desc');
  const previewEl = overlay.querySelector('#task-desc-preview');
  if (!titleEl || !descEl || !titleEl.isConnected) return; // modal closed while the fetch was in flight

  // maxlength="255" on #task-title is NOT enforced for a programmatic .value write, and the
  // API hard-rejects a >255-char title (400) on the finalize PATCH — that would surface as
  // "Failed to create task" with the reserved key still booked. Truncate defensively.
  if (title) {
    titleEl.value = title.slice(0, 255);
    // (C1512) titleEl carries its own 'input' -> persistDraft() listener (see
    // openCreateTaskModal's onMount below) — a programmatic .value write doesn't fire
    // 'input' on its own, so an imported card with a title but no description would
    // otherwise never get saved into the draft.
    titleEl.dispatchEvent(new Event('input', { bubbles: true }));
  }
  if (description) {
    descEl.value = description;
    // One synthetic input drives both C1446's preview re-render (the listener a few lines
    // below) and C1447's thumbnail strip (embed-menu.js's own 'input' listener).
    descEl.dispatchEvent(new Event('input', { bubbles: true }));
    // C1446 pins previewEl.style.height on every blur and never clears it; the preview box
    // is overflow-y:auto, so without clearing it here the freshly-imported (much longer)
    // content renders scrolled out of sight behind the old, smaller pinned height. C1446
    // re-pins it on the next blur, so its own button-jump protection is unaffected.
    if (previewEl && !previewEl.hidden) previewEl.style.height = '';
  }
}

// opts.priority (TPT30) — an explicit sprint to prefill, e.g. from the sprint board's own
// per-lane "+ Add item" tile (sprint-board.js's onAddTask). Omitted for the header's
// "+ New Task" button, which keeps the old default-newest-sprint behavior.
function openCreateTaskModal(opts = {}) {
  // (C1512) Restore an in-progress draft for this project, if one survived a previous
  // accidental dismissal (Escape/backdrop/navigation) — see lib/task-draft.js. null when
  // there's nothing saved, or storage held only garbage.
  const draft = loadNewTaskDraft(state.projectId);

  const defaultPriority = Number.isFinite(opts.priority) ? opts.priority : defaultSprintNumber(state.sprints);
  const singular = groupLabelSingular(state.project && state.project.task_group_label);
  // (C1512) A restored sprint number that no longer matches any real sprint/task priority
  // self-heals: sprintOptionsHtml() adds `currentPriority` to its option set whenever it
  // isn't already present, so this can never render a <select> with no selected option.
  // An explicit lane click (opts.priority) always wins over a stale saved draft's sprint —
  // the user just told us which lane they meant.
  const restorePriority = Number.isFinite(opts.priority) ? defaultPriority
    : (draft && draft.priority != null ? draft.priority : defaultPriority);
  const sprintOpts = sprintOptionsHtml(state.sprints, state.tasks, restorePriority, singular);
  // C1334 — sprints hidden: no sprint field in the form; new tasks silently take
  // defaultPriority (the newest sprint) — the same value the field would have pre-selected.
  const showSprintField = sprintsOn();

  // Modal-scoped reservation state, closed over by reserveKey/onMount/onClose below.
  let reservedKey = null;
  let committed = false;
  // (C1483) Guards a category-switch re-reservation racing a stale resolve — same
  // pattern as quick-create.js's reserveSeq/backlog.js's inlineReserveSeq.
  let reserveSeq = 0;
  // (C1512) A restored assignee id is trusted only when it's still a real project member
  // (or members haven't loaded yet, in which case there's nothing to validate against) —
  // a member removed from the project since the draft was saved falls back to self, same
  // as the no-draft default below.
  const knownMemberIds = new Set(state.members.map(m => m.user_id || m.id));
  const restoredAssignee = draft && draft.assignee != null
    && (knownMemberIds.size === 0 || knownMemberIds.has(draft.assignee))
    ? draft.assignee : null;
  // Default matches POST /tasks/reserve's own server-side default (the calling user) —
  // picking Unassigned is now reachable, self-assign stays the default behavior.
  let assignee = restoredAssignee ?? ((getUser() || {}).id ?? null);
  let assigneeCombo = null;
  let embedControls = null;
  let tagTypeahead = null;
  // (C1512) Attachments uploaded into THIS draft's description — tracked separately from
  // the description text itself (which only ever derives the visible thumbnail strip, see
  // lib/attachment-preview.js) so discardDraft() below deletes exactly what this modal
  // created, never an image/file ref the user hand-pasted in from somewhere else.
  let uploads = draft && draft.uploads ? draft.uploads.slice() : [];
  // Set at the top of onMount so persistDraft()/discardDraft() can read live field values
  // without threading `overlay` through every helper individually.
  let modalOverlay = null;

  const body = `
    <form id="create-task-form">
      <div class="form-group">
        <label class="form-label" for="task-key">Task Key</label>
        <input class="form-input" id="task-key" name="task_key" readonly value="Reserving…">
      </div>
      <div class="form-group">
        <label class="form-label" for="task-title">Title *</label>
        <input class="form-input" id="task-title" name="title" required maxlength="255">
      </div>
      <div class="form-group">
        <label class="form-label">Assignee</label>
        <div class="create-assignee-combo">${renderMemberCombo({ id: 'create-assignee', assignee, members: state.members })}</div>
      </div>
      <div class="form-group">
        <label class="form-label" for="task-desc">Description</label>
        <textarea class="form-textarea create-task-desc" id="task-desc" name="description"></textarea>
        <div class="md-preview create-task-desc-preview" id="task-desc-preview" title="Click to edit" hidden></div>
      </div>
      <div class="form-group">
        <label class="form-label" for="task-category">Category</label>
        <select class="form-select" id="task-category" name="category">
          <option value="CODING">Coding</option>
          <option value="HUMAN">Human</option>
        </select>
      </div>
      ${showSprintField ? `
      <div class="form-group">
        <label class="form-label" for="task-priority">${singular}</label>
        <select class="form-select" id="task-priority" name="priority">
          ${sprintOpts}
          <option value="new">+ New ${singular}</option>
          ${state.sprints.length === 0 ? `<option value="0" selected>${singular} 1 (priority 0)</option>` : ''}
        </select>
      </div>` : ''}
      <div class="form-group">
        <label class="form-label" for="task-tags">Tags</label>
        <input class="form-input" id="task-tags" placeholder="Add tag...">
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-ghost" id="modal-cancel">Cancel</button>
        <button type="submit" class="btn btn-primary" id="create-task-btn" disabled>Create Task</button>
      </div>
    </form>`;

  // Book the task_key server-side the moment the modal opens (one atomic round-trip
  // — the server guarantees a fresh, collision-proof key, so no client-side
  // guess-and-retry is needed here) so no other tab/user can take it before this
  // one submits.
  // (C1483) `category` picks CODING (this project's own task_prefix, e.g. TPT) vs
  // HUMAN (always H) — also called from the Category select's 'change' listener below
  // so the displayed key always matches whichever category is currently selected,
  // instead of staying stuck on whatever was picked (always CODING, before this fix)
  // when the modal first opened.
  async function reserveKey(overlay, category) {
    const mySeq = ++reserveSeq;
    const keyInput = overlay.querySelector('#task-key');
    const createBtn = overlay.querySelector('#create-task-btn');
    if (keyInput) keyInput.value = 'Reserving…';
    if (createBtn) createBtn.disabled = true;
    try {
      const task = await reserveTaskKey(state.projectId, { category, priority: defaultPriority });
      if (mySeq !== reserveSeq) {
        // Superseded by another category switch (or modal teardown) before this
        // resolved — release immediately, don't touch DOM/state a newer call owns.
        releaseTaskKey(state.projectId, task.task_key);
        return;
      }
      // Track it in local state (so e.g. a second modal's own reservation call
      // reflects it) but do NOT trigger renderActiveView() here: the active
      // sub-view (kanban/sprint) tears down and rebuilds on every render, and
      // its cleanup unconditionally calls closeModal() — that would immediately
      // release the key we just booked. The board picks up the reservation
      // naturally once this modal closes.
      state.tasks.push(task);
      reservedKey = task.task_key;
      if (keyInput) keyInput.value = reservedKey;
      if (createBtn) createBtn.disabled = false;
    } catch (err) {
      if (mySeq !== reserveSeq) return;
      showToast(err.message || 'Failed to reserve task key', 'error');
      closeModal();
    }
  }

  // (C1483) Category changed before submit: the already-reserved key belongs to the
  // OLD category's prefix (TPT### for CODING, H### for HUMAN) — release it and book a
  // fresh one matching the new selection. `committed` guards the narrow window between
  // submit's PATCH resolving and the modal actually closing, where the select is still
  // technically live but the key is no longer a reservation.
  function switchReservationCategory(overlay, category) {
    if (committed) return;
    const oldKey = reservedKey;
    reservedKey = null;
    if (oldKey) {
      releaseTaskKey(state.projectId, oldKey);
      state.tasks = state.tasks.filter(t => t.task_key !== oldKey);
    }
    reserveKey(overlay, category);
  }

  // (C1512) Snapshot every live field into localStorage. Bound to each field's own change
  // event from onMount, AFTER the draft-restore prefill runs there — so reopening a draft
  // can never immediately resave it back as empty.
  function persistDraft() {
    if (!modalOverlay) return;
    const titleEl = modalOverlay.querySelector('#task-title');
    const descEl = modalOverlay.querySelector('#task-desc');
    const categoryEl = modalOverlay.querySelector('#task-category');
    const priorityEl = modalOverlay.querySelector('#task-priority');
    let priority = defaultPriority;
    if (showSprintField && priorityEl) {
      const n = parseInt(priorityEl.value, 10);
      // priorityEl.value === 'new' (unconfirmed "+ New Sprint") parses to NaN — fall back
      // to defaultPriority rather than persist a not-yet-real sprint number.
      if (Number.isFinite(n)) priority = n;
    }
    saveNewTaskDraft(state.projectId, {
      title: titleEl ? titleEl.value : '',
      description: descEl ? descEl.value : '',
      tags: tagTypeahead ? tagTypeahead.getTags() : [],
      category: categoryEl ? categoryEl.value : 'CODING',
      assignee,
      priority,
      uploads,
    });
  }

  // recordUpload({kind, url}) — passed as onUpload to both upload paths (paste/drop via
  // attachImageHandlers, the Embed picker via attachEmbedControls) so this modal always
  // knows exactly which attachments it created, regardless of which path produced them.
  function recordUpload(upload) {
    uploads.push(upload);
    persistDraft();
  }

  // discardDraft() — the user explicitly said "throw this away" (Cancel or the header ×,
  // wired in onMount below): erase the stored draft AND delete every attachment it
  // uploaded. Fire-and-forget, same discipline as releaseTaskKey() above — a 404 from an
  // already-deleted row is fine, there's nothing useful to do with a failure here.
  function discardDraft() {
    clearNewTaskDraft(state.projectId);
    for (const u of uploads) {
      const path = attachmentDeletePath(u.url);
      if (path) api.delete(path).catch(() => {});
    }
    uploads = [];
  }

  openModal('New Task', body, {
    onMount(overlay) {
      modalOverlay = overlay; // (C1512) lets persistDraft()/discardDraft() read live fields

      const comboRoot = overlay.querySelector('.create-assignee-combo');
      assigneeCombo = createMemberCombo(comboRoot, {
        getMembers: () => state.members, // live — page-load members fetch may still be in flight
        getValue: () => assignee,
        setValue: (id) => { assignee = id; persistDraft(); }, // (C1512)
        onCommit: () => {}, // nothing to PATCH until submit — the row isn't finalized yet
        isAlive: () => document.body.contains(comboRoot),
        // overlay is ui.js's fixed full-viewport .modal-overlay; the real scroller is
        // its .modal child (max-height: 85vh; overflow-y: auto) — same as the Add Child
        // modal's combo in task-detail.js.
        scrollEl: overlay.querySelector('.modal') || overlay,
      });

      // Existing tags only (C1511) — a name typed here that isn't already registered has
      // no way to become a chip, so the finalize PATCH below can never 400 with
      // TAGS_UNREGISTERED (resolveTagIds(), api/src/lib/task-service.js) again. Creating a
      // brand-new tag stays a task-detail-panel action (its own typeahead instance passes
      // allowCreate:true) — this modal has no room for the required-description form.
      tagTypeahead = attachTagTypeahead(overlay.querySelector('#task-tags'), {
        projectId: state.projectId,
        initialTags: draft && draft.tags ? draft.tags : [], // (C1512) restore draft chips
        scrollEl: overlay.querySelector('.modal') || overlay,
        allowCreate: false,
        onChange: () => persistDraft(), // (C1512)
        isAlive: () => document.body.contains(overlay),
      });

      // Wire paste/drop upload immediately (not after reservation resolves) — the
      // () => reservedKey thunk resolves the real key lazily at upload time, so a paste
      // in the first ~200ms of the modal still stamps task_images.task_key correctly.
      // allowFiles:true (C1447) — this field also gets the Embed ▾ picker below, so
      // dropping/pasting a non-image file must work too, not just images.
      const descEl = overlay.querySelector('#task-desc');
      if (descEl) attachImageHandlers(descEl, state.projectId, () => reservedKey, { allowFiles: true, onUpload: recordUpload });

      // Markdown edit/preview toggle (C1446) — mirrors task-detail.js's click-to-edit/
      // blur-to-render pattern, but keeps `descEl` in the DOM (never replaceWith) since
      // the submit handler below reads it as `form.description.value`: swapping it out
      // for a rendered div would make that a form.elements miss. Preview shown only on
      // blur with non-empty content — an empty preview box in a create form is just noise.
      const previewEl = overlay.querySelector('#task-desc-preview');
      if (descEl && previewEl) {
        descEl.addEventListener('blur', () => {
          if (!descEl.value.trim()) return;
          // Pin the swapped-in element's height to the outgoing one's BEFORE toggling
          // hidden, in both directions — the Create Task button sits right below this
          // field, and blur fires on the button's own mousedown; any height jump here
          // would move the button out from under the still-in-flight click.
          previewEl.style.height = descEl.offsetHeight + 'px';
          previewEl.innerHTML = renderMarkdown(descEl.value);
          descEl.hidden = true;
          previewEl.hidden = false;
        });
        previewEl.addEventListener('click', () => {
          descEl.style.height = previewEl.offsetHeight + 'px';
          previewEl.hidden = true;
          descEl.hidden = false;
          descEl.focus();
          const len = descEl.value.length;
          descEl.setSelectionRange(len, len);
        });
        // Async image upload (lib/image-paste.js) rewrites descEl.value and dispatches
        // 'input' after blur may already have rendered the preview from the placeholder
        // text — re-render so the preview never shows a stale "uploading..." ref.
        descEl.addEventListener('input', () => {
          if (!previewEl.hidden) previewEl.innerHTML = renderMarkdown(descEl.value);
        });
      }

      // Embed ▾ button + live thumbnail strip (C1447) — anchored after previewEl (not
      // descEl) so the block sits below whichever of textarea/preview C1446's toggle is
      // currently showing, rather than wedging between them. Kept visible through both
      // toggle states rather than hidden on blur: hiding it would shrink the field by the
      // strip's height at the exact moment blur fires (the Create button's own
      // mousedown, line ~193 above) — the same button-jump C1446's height-pinning exists
      // to prevent, reintroduced one element lower.
      if (descEl && previewEl) {
        embedControls = attachEmbedControls(descEl, {
          projectId: state.projectId,
          taskKey: () => reservedKey,
          anchor: previewEl,
          scrollEl: overlay.querySelector('.modal') || overlay,
          onUpload: recordUpload, // (C1512)
          // Import ▾ (C1448) — Task App sub-nav order (Jira / Trello / Gmail). All three
          // now open their own picker: Trello (C1448), Gmail (C1449), Jira (C1450).
          imports: [
            { action: 'jira', label: 'From Jira' },
            { action: 'trello', label: 'From Trello' },
            { action: 'gmail', label: 'From Gmail' },
          ],
          onImport: (action) => {
            // .member-combo-dropdown is z-index 320, above the picker's 260 — with
            // .embed-block's mousedown->preventDefault the assignee input never blurs on
            // its own, so an open combo would paint over the picker. Same for the tag
            // typeahead's own dropdown (z-index 320 too).
            assigneeCombo?.close();
            tagTypeahead?.close();
            const onPick = (payload) => applyImportPrefill(overlay, payload);
            if (action === 'trello') { openTrelloCardPicker(state.projectId, { onPick }); return; }
            if (action === 'gmail') { openGmailThreadPicker({ onPick }); return; }
            if (action === 'jira') { openJiraIssuePicker({ onPick }); return; }
          },
        });
      }

      // (C1512) Restore prefill — placed after attachEmbedControls() above so the
      // synthetic 'input' dispatch below reaches its 'input' listener
      // (renderAttachmentStrip) and repaints the thumbnail strip for any restored
      // image/file refs already sitting in the description text.
      const titleEl = overlay.querySelector('#task-title');
      const categoryEl = overlay.querySelector('#task-category');
      if (draft) {
        if (draft.title) titleEl.value = draft.title;
        if (draft.category) categoryEl.value = draft.category;
        if (draft.description && descEl) {
          descEl.value = draft.description;
          descEl.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }

      // (C1512) Persist on every field change — bound AFTER the restore above, so
      // reopening a draft can never immediately resave it back as empty.
      titleEl.addEventListener('input', persistDraft);
      categoryEl.addEventListener('change', persistDraft);
      // (C1483) Re-reserve on every Category switch so the readonly key field always
      // shows a key with the matching prefix (CODING -> project's task_prefix, HUMAN ->
      // H) instead of silently keeping the OLD category's key past submit.
      categoryEl.addEventListener('change', () => switchReservationCategory(overlay, categoryEl.value));
      if (descEl) descEl.addEventListener('input', persistDraft);
      if (showSprintField) overlay.querySelector('#task-priority')?.addEventListener('change', persistDraft);

      // (C1483) categoryEl.value already reflects any draft-restored category (set
      // above) — reserve to match it, not a hardcoded 'CODING'.
      reserveKey(overlay, categoryEl.value);

      overlay.querySelector('#modal-cancel').addEventListener('click', () => { discardDraft(); closeModal(); });
      // (C1512) Capture-phase — openModal() registers #modal-close's own bubble-phase
      // `click -> closeModal()` listener BEFORE onMount runs, so a bubble-phase listener
      // added here would fire too late (closeModal() already ran). Capture always
      // completes before any bubble-phase handler anywhere fires, regardless of
      // registration order — same trick embed-menu.js uses for its Escape handler. The ×
      // is an explicit "I'm done with this" action, same as Cancel: erase the draft.
      overlay.addEventListener('click', (e) => {
        if (e.target.closest('#modal-close')) discardDraft();
      }, true);
      overlay.querySelector('#create-task-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!reservedKey) return; // reservation still in flight; button is disabled anyway

        const form = e.target;
        let priority;
        if (!showSprintField) {
          // C1334 — field hidden → submit exactly what it would have pre-selected (the
          // reservation at line ~127 already used this same value). Never let a hidden
          // control silently change what gets submitted.
          priority = defaultPriority;
        } else {
          const priorityVal = form.priority.value;
          if (priorityVal === 'new') {
            priority = Math.max(0, ...state.tasks.map(t => t.priority)) + 1;
          } else {
            priority = parseInt(priorityVal, 10);
          }
        }
        const tagsRaw = tagTypeahead ? tagTypeahead.getTags() : [];

        const btn = overlay.querySelector('#create-task-btn');
        btn.disabled = true;
        btn.textContent = 'Creating...';

        try {
          // Finalize the already-reserved task_key in place — never a second create.
          const { task } = await api.patch(`/projects/${state.projectId}/tasks/${reservedKey}`, {
            title: form.title.value.trim(),
            description: form.description.value,
            category: form.category.value,
            priority,
            tags: tagsRaw.length ? tagsRaw : undefined,
            // Always sent (never conditional) so picking Unassigned writes null instead
            // of leaving the reservation's self-assigned default in place.
            assignee,
            // Web-created top-level tasks are always objectives — no type selector on
            // this modal, always no parent_id (C1341).
            is_objective: true,
          });
          const idx = state.tasks.findIndex(t => t.task_key === reservedKey);
          if (idx >= 0) state.tasks[idx] = task;
          committed = true;
          // (C1512) Clear only — never discardDraft(): the uploads tracked in `uploads`
          // now belong to the just-created task and must survive.
          clearNewTaskDraft(state.projectId);
          closeModal();
          showToast('Task created', 'success');
          refreshFilterBar();
          renderActiveView();
        } catch (err) {
          btn.disabled = false;
          btn.textContent = 'Create Task';
          showToast(err.message || 'Failed to create task', 'error');
        }
      });
    },
    onClose() {
      // Destroy first, unconditionally — leaked scroll/resize listeners + a stray
      // body-appended dropdown node would otherwise survive a successful create too
      // (mirrors task-detail.js's Add Child modal onClose).
      if (assigneeCombo) { assigneeCombo.destroy(); assigneeCombo = null; }
      if (tagTypeahead) { tagTypeahead.destroy(); tagTypeahead = null; }
      if (embedControls) { embedControls.destroy(); embedControls = null; }
      // Orphan-overlay guard (C1448, generalized C1449): if this modal is closing while an
      // Import ▾ picker (Trello, Gmail, Jira) is still open above it (Escape reaching this
      // modal, backdrop click, route navigation), nothing else would tear the picker down
      // — it would survive as a full-viewport overlay with a live capture-phase Escape
      // handler. The shared singleton in lib/import-picker-shell.js means this one call
      // covers every picker kind. No-ops if none open.
      closeImportPicker();
      // Modal dismissed (Cancel, ×, Esc, backdrop, or route navigation) before submit:
      // release the booked key so it's free for the next task.
      if (committed || !reservedKey) return;
      const key = reservedKey;
      reservedKey = null;
      releaseTaskKey(state.projectId, key);
      state.tasks = state.tasks.filter(t => t.task_key !== key);
      renderActiveView();
    },
  });
}

// ── Filter bar ──

function onFilterChange(filters, changedKey) {
  state.filters = filters;
  setQueryParams(filtersToParams(filters));
  // TPT25 — Clear All means "forget this project's saved selection too", every other
  // mutation (including the now-empty state a single chip-remove can produce) updates it.
  if (changedKey === 'clear') clearFilterPrefs(state.projectId);
  else saveFilterPrefs(state.projectId, filters);
  // C1524 — a search-only change must not rebuild #board-filters: #bf-search is focused
  // and mid-typing, and the innerHTML wipe would take the caret (and any untrimmed
  // trailing space) with it. The chip row + Clear All are the only bar content filters.q
  // affects.
  if (changedKey === 'q') syncFilterChips(state.filters, state.members, state.sprints, filterOpts());
  else refreshFilterBar();
  renderActiveView();
}

// Shared by refreshFilterBar() and the q-only syncFilterChips() path (C1524) so both agree
// on the options renderBoardFilters/renderFilterChips render against.
function filterOpts() {
  const plural = groupLabelPlural(state.project && state.project.task_group_label);
  const singular = groupLabelSingular(state.project && state.project.task_group_label);
  return { activeView: state.activeView, editBoardMode: state.editBoardMode, canEditWorkflow: canEditWorkflow(), groupLabel: plural, groupLabelSingular: singular, sprintsEnabled: sprintsOn() };
}

function onToggleEditBoard() {
  state.editBoardMode = !state.editBoardMode;
  refreshFilterBar();
  renderActiveView();
}

// Bubbles a successful column-reorder drop (kanban.js) back up so the next
// refreshFilterBar()/renderActiveView() pass (e.g. switching view, editing a status
// name elsewhere) sees the fresh display_order — no extra round-trip needed, the
// reorder endpoint already returns the full ordered list.
function onStatusesChanged(newStatuses) {
  state.statuses = newStatuses || state.statuses;
}

// Sub-views render the *derived* `filtered` array from renderActiveView() (applyFilters
// output), never state.tasks itself — so they must NOT refetch into their own array: with
// any filter active that array is a throwaway copy, and overwriting it in place repaints
// the board unfiltered while the bar still shows the active filter (C1261). They bubble
// "server data changed" up to this instead; same refetch → refreshFilterBar() →
// renderActiveView() idiom as the Trello import and sprint-manager handlers below.
async function reloadTasks() {
  // task-detail's onClose fires ~220ms after close (see task-detail.js) — can land after
  // boardPage()'s teardown already ran resetState(). Bail instead of GET /projects/null/tasks.
  if (!state.projectId) return;
  try {
    const { tasks } = await api.get('/projects/' + state.projectId + '/tasks');
    state.tasks = tasks || [];
  } catch (_) {}
  refreshFilterBar();
  renderActiveView();
}

function refreshFilterBar() {
  const allTags = [...new Set(state.tasks.flatMap(t => t.tags || []))].sort();
  const el = document.getElementById('board-filters');
  if (el) {
    const opts = filterOpts();
    // C1524 — every other trigger of this full rebuild (status/type/assignee/tag/sprint
    // toggles, Clear All, reloadTasks()'s ~220ms-delayed drawer-close refetch, sprint
    // manager updates, create-task success, page load) can land while #bf-search is
    // focused mid-keystroke. Capture before the innerHTML wipe, restore after rebind.
    const focusSnap = captureFilterFocus();
    el.innerHTML = renderBoardFilters(state.filters, state.members, allTags, state.sprints, state.tasks, state.statuses, opts);
    bindBoardFilters(state.filters, onFilterChange, state.members, allTags, state.sprints, state.tasks, state.statuses, opts, onToggleEditBoard);
    restoreFilterFocus(focusSnap);
  }
}

// ── View switching ──

function renderActiveView() {
  if (state.subViewCleanup) {
    state.subViewCleanup();
    state.subViewCleanup = null;
  }

  const viewport = document.getElementById('board-viewport');
  if (!viewport) return;
  viewport.innerHTML = '';

  // (TPT112) #content is the same <main> element for the life of the SPA session
  // (layout.js's initLayout() early-returns once the shell is mounted), so this
  // class must be kept in sync with the active view here AND explicitly removed
  // in boardPage()'s teardown below — a leaked class would turn every other page
  // into a height-bounded flex column. Scoped to kanban only: the sprint view
  // renders into this same #board-viewport and must keep growing the page.
  getContentEl().classList.toggle('board-page--kanban', state.activeView === 'kanban');

  const filtered = applyFilters(state.tasks, state.filters);
  const filtersActive = hasActiveFilters(state.filters);

  if (state.tasks.length === 0) {
    viewport.innerHTML = '<div class="empty">No tasks yet. Create one to get started.</div>';
    return;
  }
  // Kanban must keep rendering all five status columns even when the current
  // sprint selection matches zero tasks, so every status stays a valid
  // drag-and-drop / quick-create target (C1042). The sprint selector itself
  // lives in the always-visible filter bar above #board-viewport (C1166), so
  // this exception is no longer about selector reachability.
  if (filtered.length === 0 && state.activeView !== 'kanban') {
    viewport.innerHTML = '<div class="empty">No tasks match your filters.</div>';
    return;
  }

  switch (state.activeView) {
    case 'kanban':
      state.subViewCleanup = renderKanbanView(viewport, filtered, state.projectId, state.members, state.statuses, {
        editBoardMode: state.editBoardMode && canEditWorkflow(),
        onStatusesChanged,
        onDataChanged: reloadTasks,
      });
      break;
    case 'sprint':
    default: {
      const groupLabel = groupLabelSingular(state.project && state.project.task_group_label);
      state.subViewCleanup = renderSprintView(viewport, filtered, state.projectId, state.members, state.sprints, { filtersActive, groupLabel, onDataChanged: reloadTasks, onAddTask: (priority) => openCreateTaskModal({ priority }), sprintsEnabled: sprintsOn() }, state.statuses);
      break;
    }
  }
}

function onViewChange(viewType) {
  state.activeView = viewType;
  setViewPref(state.projectId, viewType);
  renderActiveView();
}

// ── Page entry point ──

export async function boardPage(params) {
  if (!(await checkAuth())) return;
  resetState();
  state.projectId = params.id;
  state.activeView = getViewPref(params.id);

  initLayout(getUser());
  updateLayoutContext({ projectId: params.id, projectName: null, activeView: 'board', viewLabel: 'Board' });

  const content = getContentEl();
  content.innerHTML = `
    <div class="page-header board-header">
      <h2>Board</h2>
      ${renderViewSwitcher(state.activeView)}
      <button class="btn btn-ghost btn-sm" id="manage-sprints-btn">Sprints</button>
      <button class="btn btn-ghost btn-sm" id="trello-import-btn">Import from Trello</button>
      <button class="btn btn-primary btn-sm" id="new-task-btn">+ New Task</button>
    </div>
    <div id="board-filters"></div>
    <div id="board-viewport"><div class="empty"><span class="spinner"></span></div></div>`;

  // Header button handlers
  bindViewSwitcher(onViewChange);
  document.getElementById('new-task-btn').addEventListener('click', () => openCreateTaskModal());
  document.getElementById('trello-import-btn').addEventListener('click', () => {
    openTrelloImportModal(state.projectId, reloadTasks);
  });

  document.getElementById('manage-sprints-btn').addEventListener('click', () => {
    openSprintManager(state.projectId, {
      // (C1395) resolved from state.project (fetched below, may still be null on a very
      // fast click before the page-load Promise.all resolves) — openSprintManager()
      // itself defaults to 'Sprint'/'Sprints' when omitted.
      groupLabel: {
        plural: groupLabelPlural(state.project && state.project.task_group_label),
        singular: groupLabelSingular(state.project && state.project.task_group_label),
      },
      onUpdate: async () => {
        try {
          const res = await api.get('/projects/' + state.projectId + '/sprints');
          state.sprints = res.sprints || [];
          // Sprint names/closed-status live in the selector's option labels now
          // (moved off the kanban sub-view in C1166) — refresh the bar too, or
          // a rename/close here leaves stale labels/dimming in the dropdown.
          refreshFilterBar();
          renderActiveView();
        } catch (_) {}
      },
    });
  });

  try {
    const [tasksRes, membersRes, sprintsRes, statuses, project] = await Promise.all([
      api.get('/projects/' + params.id + '/tasks'),
      api.get('/projects/' + params.id + '/members'),
      api.get('/projects/' + params.id + '/sprints'),
      loadStatuses(params.id),
      // C1234/C1395 — task_group_label for board title bars, via the shared per-project
      // cache (lib/project.js) so a Settings-page save's invalidateProject() is visible
      // here on the next visit without a hard reload. Fails open (null) on its own, so a
      // fetch hiccup falls back to the default label instead of killing the whole board.
      loadProject(params.id),
    ]);
    state.tasks = tasksRes.tasks || [];
    state.members = membersRes.members || [];
    state.sprints = sprintsRes.sprints || [];
    state.statuses = statuses || [];
    state.project = project;

    // Header button label follows the project's task_group_label (C1234) — set
    // after the fetch resolves since the header HTML above renders before it does.
    // C1334 — sprints hidden: drop the button entirely. `.remove()`, not `.hidden` — `.btn
    // { display: inline-flex }` (styles.css) out-specifies the UA `[hidden]` rule (same
    // trap `.pwa-install-btn[hidden]`/`.settings-pane[hidden]` work around).
    const manageBtn = document.getElementById('manage-sprints-btn');
    if (manageBtn) {
      if (!sprintsOn()) {
        manageBtn.remove();
      } else {
        manageBtn.textContent = groupLabelPlural(state.project && state.project.task_group_label);
      }
    }
    // (C1395) view-switcher's 'Sprint Board' tooltip — same late-refresh reason as the
    // manage-sprints button above (rendered before this fetch resolves).
    updateViewSwitcherLabel(groupLabelSingular(state.project && state.project.task_group_label));

    // Restore filters — TPT25: an explicit URL query wins (shareable link); a bare hash
    // (every route navigation drops the query entirely — see router.js's resolve()) falls
    // back to this project's last saved selection instead of resetting to defaults.
    const queryParams = getQueryParams();
    const sourceParams = hasFilterParams(queryParams) ? queryParams : (loadFilterPrefs(state.projectId) || queryParams);
    state.filters = { ...FILTER_DEFAULTS, ...filtersFromParams(sourceParams) };

    // A saved/URL status name this project's workflow no longer has (renamed/deleted since
    // it was written) would otherwise filter the board to zero rows with no checkbox left
    // to explain why — drop anything not in the freshly-loaded status list.
    const knownStatusNames = new Set(statusNames(state.statuses));
    state.filters.statuses = state.filters.statuses.filter(s => knownStatusNames.has(s));

    if (!sprintsOn()) {
      // C1334 — sprints hidden: the flat list must be the WHOLE list. Force "all" even when
      // the source carries an explicit sprint selection (stale bookmark/pref, or the toggle
      // flipped in another tab) — with the selector and chips gated out of the filter bar
      // there is no control left to clear it with, and a silently-truncated "flat list" is a
      // lie. `[]` serializes as the explicit `sprint=all` sentinel, so a reload stays stable.
      state.filters.sprints = [];
    } else if (!sourceParams.has('sprint')) {
      // No sprint selection anywhere (URL nor saved pref) → default to the 5 most recent
      // sprints instead of showing everything unfiltered.
      const allNumbers = sprintFilterNumbers(state.sprints, state.tasks);
      state.filters.sprints = applySprintPresets(allNumbers, 'recent');
    }
    // Write back so the URL reflects what's actually shown (shareable) — covers both
    // branches above plus a plain "restored from saved prefs, URL was bare" mount.
    setQueryParams(filtersToParams(state.filters));

    // Render filter bar + board
    refreshFilterBar();
    renderActiveView();
  } catch (err) {
    document.getElementById('board-viewport').innerHTML =
      `<div class="empty text-muted">Failed to load tasks</div>`;
  }

  return () => {
    unbindBoardFilters();
    removeAssigneePicker();
    closeTaskDetail();
    if (state.subViewCleanup) state.subViewCleanup();
    closeModal();
    // (TPT112) #content outlives this route — see renderActiveView() above.
    getContentEl().classList.remove('board-page--kanban');
    resetState();
  };
}
